import { useRef, useState } from "react";
import { Loader2, Search, Users, X } from "lucide-react";
import type { ShareRole, Visibility } from "@hector/shared-types/resource-sharing";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import type { EnrichedCabinetMember } from "@/hooks/cabinet/types";
import {
  cabinetOptionMatchesQuery,
  filterAddableMembers,
  filterMembersByQuery,
  PERSON_ROLE_OPTIONS,
  type ShareGrant,
} from "./shareControlUtils";
import type { ShareSaveStatus } from "./useShareController";

// Ré-export pour les consommateurs/stories qui importent `ShareGrant` depuis ce fichier.
export type { ShareGrant };

/**
 * Classe du `<SelectTrigger>` de rôle en mode « fantôme » (design B, 2026-07-07) :
 * neutralise la bordure / le fond / l'ombre / la hauteur fixe du trigger shadcn
 * pour le réduire à un libellé texte fin + petit chevron. `[&>svg]` cible le
 * `ChevronDown` (dernier enfant SVG du trigger) pour le rétrécir à 10px.
 *
 * `!shadow-none` (important) est nécessaire : l'ombre du trigger est `shadow-soft`,
 * une classe CUSTOM (tailwind.config) que `twMerge` ne reconnaît PAS comme du
 * même groupe que `shadow-none` → sans le `!`, les deux cohabitent et l'ombre
 * subsiste (pilule blanche résiduelle autour du rôle).
 */
const GHOST_ROLE_TRIGGER =
  "h-auto w-auto justify-start gap-0.5 border-0 bg-transparent px-1 py-0.5 text-[11px] text-grey-500 !shadow-none hover:border-transparent hover:text-grey-900 hover:!shadow-none [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-grey-400";

export interface ShareControlProps {
  /** 'private' = personne d'autre ; 'cabinet' = tout le cabinet en édition. */
  visibility: Visibility;
  onVisibilityChange: (v: Visibility) => void;
  /** partages nominatifs actuels (hors owner). */
  shares: ShareGrant[];
  /** ajoute une personne (le hook de contrôle choisit le rôle par défaut = 'editor'). */
  onAddPerson: (userId: string) => void;
  onChangePersonRole: (userId: string, role: ShareRole) => void;
  onRemovePerson: (userId: string) => void;
  members: EnrichedCabinetMember[] | undefined;
  /** owner de la ressource — exclu du picker (droits déjà acquis par propriété). */
  ownerId: string;
  /** chargement (membres/shares) → skeleton + contrôles désactivés. */
  isLoading?: boolean;
  /** user_id dont une mutation nominative est en vol → sa ligne désactivée. */
  pendingUserId?: string | null;
  /** visibilité en cours de mutation → ligne cabinet désactivée. */
  visibilityPending?: boolean;
  /**
   * Retour d'auto-enregistrement affiché sur le bouton de bas de modale. Fourni
   * par `useShareController` en mode live (modales) ; omis en mode brouillon
   * (création de dossier) où rien n'est encore persisté.
   */
  saveStatus?: ShareSaveStatus;
  /**
   * Fournie par les modales (live) : rend un bouton en bas à droite qui reflète
   * `saveStatus` (« Terminé » → « Enregistrement… ») et ferme le panneau au
   * clic. Omise en mode brouillon (le formulaire de création a son propre
   * bouton « Créer le dossier ») → aucun bouton rendu ici.
   */
  onDone?: () => void;
}

function initialsOf(m: EnrichedCabinetMember): string {
  const source = m.full_name || m.email || "?";
  const parts = source.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function labelOf(m: EnrichedCabinetMember): string {
  return m.full_name || m.email || `${m.user_id.slice(0, 8)}…`;
}

/**
 * Contrôle de partage unifié — composant PRÉSENTATIONNEL contrôlé, sans aucun
 * fetch de données (les `members` viennent en prop). Généralisation du pattern
 * recherche + liste d'accès partagé par `ShareCasePopover`/`ShareConversationPopover`/
 * `ShareTemplatePopover` pour les 3 ressources partageables (dossier /
 * conversation / modèle) : le contrat `{ visibility, shares }` + les callbacks
 * sont identiques pour les trois.
 *
 * Design « ultra-fin » (proposition B validée 2026-07-07) : recherche en barre
 * soulignée, lignes une seule ligne (nom seul, sans email), avatars 18px, rôle
 * en libellé texte fantôme + chevron discret, croix de retrait révélée au
 * survol. La recherche est une barre inline (pas un `<Popover>` imbriqué) : un
 * Popover-dans-Popover créait une deuxième surface de fermeture concurrente et
 * un aller-retour de plus pour ajouter quelqu'un ; le résultat s'affiche
 * directement sous la barre au focus/à la frappe.
 *
 * Ne rend PAS de coquille `<Dialog>`/`<Popover>` : les consommateurs (panneaux
 * de partage) l'entourent d'un `<Popover>`/`<PopoverContent>` shadcn ancré au
 * clic. Toute la logique optimiste (état "en vol", rollback) vit dans
 * `useShareController`, pas ici — ce composant ne fait que refléter les props.
 */
export function ShareControl({
  visibility,
  onVisibilityChange,
  shares,
  onAddPerson,
  onChangePersonRole,
  onRemovePerson,
  members,
  ownerId,
  isLoading = false,
  pendingUserId = null,
  visibilityPending = false,
  saveStatus = "idle",
  onDone,
}: ShareControlProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const membersList = members ?? [];
  const isCabinetVisible = visibility === "cabinet";

  // Destinataires ajoutables = membres du cabinet, owner exclu, déjà-partagés
  // exclus, et `pendingUserId` exclu aussi : pendant qu'un ajout est en vol
  // (avant que la query `shares` ne soit invalidée/refetchée), le membre reste
  // sinon proposé → un 2ᵉ clic déclencherait une mutation concurrente pour le
  // même destinataire.
  const addableMembers = filterAddableMembers(membersList, shares, ownerId).filter(
    (m) => m.user_id !== pendingUserId,
  );
  const filteredMembers = filterMembersByQuery(addableMembers, query);
  const cabinetOptionVisible = !isCabinetVisible && cabinetOptionMatchesQuery(query);

  function closeSearch() {
    setQuery("");
    setSearchOpen(false);
  }

  function selectCabinet() {
    closeSearch();
    onVisibilityChange("cabinet");
  }

  function selectMember(userId: string) {
    closeSearch();
    onAddPerson(userId);
  }

  // Membres partagés résolus, dans l'ordre de `shares` — un membre disparu du
  // cabinet entre temps (edge case) est ignoré ici plutôt que de crasher sur
  // `undefined`.
  const sharedMembers = shares
    .map((share) => {
      const member = membersList.find((m) => m.user_id === share.user_id);
      return member ? { member, role: share.role } : null;
    })
    .filter((entry): entry is { member: EnrichedCabinetMember; role: ShareRole } => entry !== null);

  const noOtherCabinetMembers =
    !isLoading && membersList.filter((m) => m.user_id !== ownerId).length === 0;

  const showEmptyState = visibility === "private" && shares.length === 0 && !noOtherCabinetMembers;
  const showResults = searchOpen && (cabinetOptionVisible || filteredMembers.length > 0);
  const showNoResults = searchOpen && !cabinetOptionVisible && filteredMembers.length === 0;

  return (
    <div className={cn("space-y-3 transition-[padding-bottom] duration-150", searchOpen && "pb-48")}>
      {/* Barre de recherche inline soulignée — « Tout le cabinet » (si pas déjà
          cabinet) + membres filtrés par nom/email, résultats affichés dessous. */}
      {noOtherCabinetMembers ? (
        <p className="py-3 text-center text-[11px] text-grey-500">
          Aucun autre membre dans ce cabinet.
        </p>
      ) : (
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-0.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-grey-400"
            aria-hidden
          />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSearchOpen(true);
            }}
            // PAS de `onFocus` : le Popover parent auto-focus son premier
            // élément focusable à l'ouverture (comportement Radix par défaut) —
            // cette barre. Ouvrir la liste sur ce focus programmatique
            // afficherait le dropdown complet dès l'ouverture du panneau, avant
            // toute action de l'utilisateur. `onClick` couvre le vrai clic.
            onClick={() => setSearchOpen(true)}
            // Fermer le dropdown dès que le focus quitte la barre (clic ailleurs
            // dans le panneau, Tab…) — sinon il n'existait aucune sortie hormis
            // Escape ou sélectionner quelqu'un, et le dropdown restait « collé »
            // par-dessus la liste (signalé 2026-07-07 : « je peux plus sortir de
            // la recherche »). Les items utilisent onMouseDown+preventDefault, qui
            // s'exécute AVANT ce blur → la sélection reste possible.
            onBlur={closeSearch}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                closeSearch();
                searchInputRef.current?.blur();
              }
            }}
            placeholder="Rechercher un membre du cabinet…"
            aria-label="Rechercher un membre du cabinet"
            disabled={isLoading}
            className="h-7 w-full border-0 border-b border-grey-200 bg-transparent pl-6 pr-2 text-[11px] text-grey-900 placeholder:text-grey-400 focus:border-grey-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
          />
          {showResults && (
            <div className="absolute inset-x-0 top-[calc(100%+4px)] z-10 max-h-72 overflow-y-auto rounded-md border border-grey-200 bg-white py-1 shadow-md">
              {cabinetOptionVisible && (
                <button
                  type="button"
                  // onMouseDown (pas onClick) + preventDefault : évite que le
                  // blur de l'input (déclenché au mousedown) ne ferme la liste
                  // AVANT que la sélection ne s'exécute.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectCabinet();
                  }}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-grey-50"
                >
                  <span className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-grey-100">
                    <Users className="h-3 w-3 text-grey-500" aria-hidden />
                  </span>
                  <span className="truncate font-medium text-grey-900">Tout le cabinet</span>
                </button>
              )}
              {filteredMembers.map((member) => (
                <button
                  key={member.user_id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectMember(member.user_id);
                  }}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-grey-50"
                >
                  <Avatar className="h-[18px] w-[18px] flex-shrink-0">
                    {member.avatar_url ? (
                      <AvatarImage src={member.avatar_url} alt={member.full_name || ""} />
                    ) : null}
                    <AvatarFallback className="bg-grey-100 text-[8px] font-semibold text-grey-600">
                      {initialsOf(member)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1 truncate text-grey-900">
                    {member.full_name || member.email || "Membre inconnu"}
                  </span>
                </button>
              ))}
            </div>
          )}
          {showNoResults && (
            <div className="absolute inset-x-0 top-[calc(100%+4px)] z-10 rounded-md border border-grey-200 bg-white px-2.5 py-1.5 text-[11px] text-grey-500 shadow-md">
              Aucun membre trouvé.
            </div>
          )}
        </div>
      )}

      {/* Liste des accès. */}
      <div
        className={cn(
          "space-y-1",
          (sharedMembers.length > 4 || isCabinetVisible) && "max-h-[22rem] overflow-y-auto pr-0.5",
        )}
      >
        {isLoading ? (
          <div className="space-y-1.5" aria-hidden>
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center gap-2 px-1 py-1">
                <Skeleton className="h-[18px] w-[18px] rounded-full" />
                <Skeleton className="h-2.5 w-32 flex-1" />
                <Skeleton className="h-4 w-14" />
              </div>
            ))}
          </div>
        ) : (
          <ul>
            {isCabinetVisible && (
              <li className="py-1">
                <div className="group flex items-center gap-2 rounded-md px-1 py-1.5">
                  <span className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-grey-100">
                    <Users className="h-3 w-3 text-grey-500" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-grey-900">
                    Tout le cabinet
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-grey-500">Édition</span>
                  <button
                    type="button"
                    className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-grey-300 transition-colors hover:text-red-600 disabled:opacity-50"
                    disabled={visibilityPending}
                    aria-label="Retirer l'accès de tout le cabinet"
                    onClick={() => onVisibilityChange("private")}
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </div>
                <p className="pl-7 pr-1 text-[10px] leading-snug text-grey-400">
                  Tout le cabinet est éditeur. Les rôles individuels ci-dessous ne
                  s'appliqueront qu'en repassant en privé.
                </p>
              </li>
            )}

            {sharedMembers.map(({ member, role }) => {
              const isPending = pendingUserId === member.user_id;
              return (
                <li
                  key={member.user_id}
                  className="group flex items-center gap-2 rounded-md px-1 py-1.5 hover:bg-grey-50"
                >
                  <Avatar className="h-[18px] w-[18px] flex-shrink-0">
                    {member.avatar_url ? (
                      <AvatarImage src={member.avatar_url} alt={member.full_name || ""} />
                    ) : null}
                    <AvatarFallback className="bg-grey-100 text-[8px] font-semibold text-grey-600">
                      {initialsOf(member)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1 truncate text-[11px] text-grey-900">
                    {labelOf(member)}
                  </span>
                  <Select
                    value={role}
                    disabled={isPending}
                    onValueChange={(v) => onChangePersonRole(member.user_id, v as ShareRole)}
                  >
                    <SelectTrigger
                      className={GHOST_ROLE_TRIGGER}
                      aria-label={`Rôle de ${member.full_name || member.email || "ce membre"}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    {/* Menu de rôle resserré pour rester cohérent avec le
                        panneau ultra-fin : le SelectContent/Item par défaut
                        (text-sm, py-2, pl-8, coche) rendait un gros bloc blanc
                        disproportionné (signalé 2026-07-07). `[&_svg]:hidden`
                        masque la coche du `SelectItem` partagé (le rôle courant
                        est déjà porté par le libellé du trigger + le fond gris
                        de la ligne active) → sélection simple, sans checkbox ;
                        pl réduit puisque plus d'espace de coche à réserver. */}
                    <SelectContent className="min-w-[6.5rem]">
                      {PERSON_ROLE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="px-3 py-1.5 text-[11px] [&_svg]:hidden"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-grey-300 opacity-0 transition-opacity hover:text-red-600 focus:opacity-100 group-hover:opacity-100 disabled:opacity-50"
                    disabled={isPending}
                    aria-label={`Retirer l'accès de ${member.full_name || member.email || "ce membre"}`}
                    onClick={() => onRemovePerson(member.user_id)}
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </li>
              );
            })}

            {showEmptyState && (
              <li className="py-3 text-center text-[11px] text-grey-500">
                Personne d'autre pour l'instant. Ajoutez le cabinet ou une personne
                ci-dessus.
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Bouton de bas de modale (live only) : auto-save conservé, ce bouton
          reflète le statut (Terminé → Enregistrement…) et ferme la modale.
          `min-w` fixe pour éviter que la largeur saute au changement de
          libellé ; `aria-live` annonce le statut aux lecteurs d'écran. */}
      {onDone && (
        <div className="flex justify-end pt-0.5">
          <Button
            type="button"
            size="sm"
            onClick={onDone}
            disabled={saveStatus === "saving"}
            aria-live="polite"
            className="h-6 min-w-[7rem] px-3 text-[11px] font-normal"
          >
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
                Enregistrement…
              </>
            ) : (
              "Terminé"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ShareControl;
